// Delivery Slot Scheduler — manage delivery time slots by zone
import { useState, useCallback, useMemo } from "react";
import { Clock, Plus, Pencil, Trash2, AlertCircle } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useDeliverySlots,
  useCreateDeliverySlot,
  useUpdateDeliverySlot,
  useDeleteDeliverySlot,
} from "@/hooks/admin/useDeliverySlots";
import { useDeliveryZones } from "@/hooks/admin/useDeliveryZones";
import type {
  AdminDeliveryZone,
  DeliverySlot,
  DeliverySlotPayload,
} from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAYS_OF_WEEK = [
  { index: 1, short: "Lun" },
  { index: 2, short: "Mar" },
  { index: 3, short: "Mer" },
  { index: 4, short: "Jeu" },
  { index: 5, short: "Ven" },
  { index: 6, short: "Sam" },
  { index: 0, short: "Dim" },
] as const;

const EMPTY_FORM: DeliverySlotPayload = {
  zone_id: "",
  label: "",
  start_hour: 9,
  end_hour: 12,
  capacity: 10,
  days_of_week: [1, 2, 3, 4, 5],
  is_active: true,
};

// ---------------------------------------------------------------------------
// Days display helper
// ---------------------------------------------------------------------------

function DaysBadges({ days }: { days: readonly number[] }) {
  return (
    <div className="flex flex-wrap gap-0.5">
      {DAYS_OF_WEEK.map(({ index, short }) => (
        <span
          key={index}
          className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
            days.includes(index)
              ? "bg-amber-100 text-amber-800"
              : "bg-stone-100 text-stone-400"
          }`}
        >
          {short}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slot form modal
// ---------------------------------------------------------------------------

interface SlotFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly slot: DeliverySlot | null;
  readonly zones: readonly AdminDeliveryZone[];
  readonly onSave: (payload: DeliverySlotPayload) => Promise<void>;
  readonly isSaving: boolean;
}

function SlotFormModal({
  open,
  onClose,
  slot,
  zones,
  onSave,
  isSaving,
}: SlotFormModalProps) {
  const [form, setForm] = useState<DeliverySlotPayload>(
    slot
      ? {
          zone_id: slot.zone_id,
          label: slot.label,
          start_hour: slot.start_hour,
          end_hour: slot.end_hour,
          capacity: slot.capacity,
          days_of_week: [...slot.days_of_week],
          is_active: slot.is_active,
        }
      : EMPTY_FORM,
  );
  const [formError, setFormError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setForm(EMPTY_FORM);
    setFormError(null);
    onClose();
  }, [onClose]);

  const updateField = useCallback(
    <K extends keyof DeliverySlotPayload>(
      key: K,
      value: DeliverySlotPayload[K],
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const toggleDay = useCallback((dayIndex: number) => {
    setForm((prev) => {
      const days = prev.days_of_week as number[];
      const next = days.includes(dayIndex)
        ? days.filter((d) => d !== dayIndex)
        : [...days, dayIndex];
      return { ...prev, days_of_week: next };
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!form.zone_id.trim()) {
      setFormError("Veuillez sélectionner une zone.");
      return;
    }
    if (!form.label.trim()) {
      setFormError("Le libellé est requis.");
      return;
    }
    if (form.start_hour >= form.end_hour) {
      setFormError("L'heure de début doit être avant l'heure de fin.");
      return;
    }
    if (form.days_of_week.length === 0) {
      setFormError("Sélectionnez au moins un jour.");
      return;
    }
    setFormError(null);
    try {
      await onSave(form);
      handleClose();
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Une erreur est survenue.",
      );
    }
  }, [form, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={slot ? "Modifier le créneau" : "Nouveau créneau"}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="gap-1.5">
            {isSaving ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : null}
            {slot ? "Enregistrer" : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Zone */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Zone
          </label>
          <select
            value={form.zone_id}
            onChange={(e) => updateField("zone_id", e.target.value)}
            className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100 disabled:opacity-50"
            disabled={zones.length === 0}
          >
            <option value="">
              {zones.length === 0
                ? "Aucune zone disponible — créez d'abord une zone"
                : "Sélectionner une zone…"}
            </option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.zone_name}
                {z.region ? ` — ${z.region.name}` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Label */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Libellé du créneau
          </label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => updateField("label", e.target.value)}
            placeholder="ex. 9h-12h"
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Hours */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              Heure de début
            </label>
            <input
              type="number"
              min={0}
              max={23}
              value={form.start_hour}
              onChange={(e) =>
                updateField("start_hour", Number(e.target.value))
              }
              className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-stone-700">
              Heure de fin
            </label>
            <input
              type="number"
              min={1}
              max={24}
              value={form.end_hour}
              onChange={(e) => updateField("end_hour", Number(e.target.value))}
              className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            />
          </div>
        </div>

        {/* Capacity */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Capacité (livraisons max)
          </label>
          <input
            type="number"
            min={1}
            value={form.capacity}
            onChange={(e) => updateField("capacity", Number(e.target.value))}
            className="h-10 w-full rounded-md border border-stone-200 px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Days of week */}
        <div>
          <label className="mb-2 block text-sm font-medium text-stone-700">
            Jours de la semaine
          </label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS_OF_WEEK.map(({ index, short }) => {
              const active = (form.days_of_week as number[]).includes(index);
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => toggleDay(index)}
                  className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                      : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                  }`}
                >
                  {short}
                </button>
              );
            })}
          </div>
        </div>

        {/* Active toggle */}
        <div className="flex items-center gap-2">
          <input
            id="slot-active"
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => updateField("is_active", e.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
          />
          <label
            htmlFor="slot-active"
            className="text-sm font-medium text-stone-700"
          >
            Créneau actif
          </label>
        </div>

        {formError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {formError}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function DeliverySlots() {
  const { data, isLoading, isError, refetch } = useDeliverySlots();
  const { data: zonesData } = useDeliveryZones();
  const createSlot = useCreateDeliverySlot();
  const updateSlot = useUpdateDeliverySlot();
  const deleteSlot = useDeleteDeliverySlot();

  const zones = zonesData?.zones ?? [];

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<DeliverySlot | null>(null);
  const [deletingSlotId, setDeletingSlotId] = useState<string | null>(null);

  const slots = data?.slots ?? [];

  const groupedByZone = useMemo(() => {
    const map = new Map<string, DeliverySlot[]>();
    for (const slot of slots) {
      const existing = map.get(slot.zone_name) ?? [];
      map.set(slot.zone_name, [...existing, slot]);
    }
    return map;
  }, [slots]);

  const handleCreateSave = useCallback(
    async (payload: DeliverySlotPayload) => {
      await createSlot.mutateAsync(payload);
      setIsCreateOpen(false);
    },
    [createSlot],
  );

  const handleEditSave = useCallback(
    async (payload: DeliverySlotPayload) => {
      if (!editingSlot) return;
      await updateSlot.mutateAsync({ id: editingSlot.id, payload });
      setEditingSlot(null);
    },
    [editingSlot, updateSlot],
  );

  const handleToggleActive = useCallback(
    (slot: DeliverySlot) => {
      updateSlot.mutate({ id: slot.id, payload: { is_active: !slot.is_active } });
    },
    [updateSlot],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingSlotId) return;
    await deleteSlot.mutateAsync(deletingSlotId);
    setDeletingSlotId(null);
  }, [deletingSlotId, deleteSlot]);

  const columns: readonly ColumnDef<DeliverySlot>[] = useMemo(
    () => [
      {
        id: "label",
        header: "Créneau",
        cell: (s) => (
          <span className="font-medium text-stone-900">{s.label}</span>
        ),
      },
      {
        id: "hours",
        header: "Horaires",
        cell: (s) => (
          <span className="text-sm text-stone-600">
            {s.start_hour}h – {s.end_hour}h
          </span>
        ),
      },
      {
        id: "capacity",
        header: "Capacité",
        cell: (s) => (
          <span className="text-sm text-stone-600">{s.capacity} livraisons</span>
        ),
      },
      {
        id: "days_of_week",
        header: "Jours",
        cell: (s) => <DaysBadges days={s.days_of_week} />,
      },
      {
        id: "is_active",
        header: "Statut",
        cell: (s) => (
          <button
            type="button"
            onClick={() => handleToggleActive(s)}
            className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
              s.is_active
                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                : "bg-stone-100 text-stone-500 hover:bg-stone-200"
            }`}
          >
            {s.is_active ? "Actif" : "Inactif"}
          </button>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (s) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setEditingSlot(s)}
              className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              aria-label="Modifier"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setDeletingSlotId(s.id)}
              className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Supprimer"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [handleToggleActive],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Créneaux de livraison
              </h1>
              <p className="text-sm text-stone-500">
                {isLoading ? "Chargement…" : `${slots.length} créneau(x) configuré(s)`}
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Nouveau créneau
          </Button>
        </div>

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>Impossible de charger les créneaux.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Réessayer
            </Button>
          </div>
        )}

        {/* Grouped by zone */}
        {!isError && !isLoading && slots.length === 0 && (
          <EmptyState
            icon={<Clock className="h-6 w-6" />}
            title="Aucun créneau configuré"
            description="Créez vos premiers créneaux de livraison par zone."
            action={{ label: "Nouveau créneau", onClick: () => setIsCreateOpen(true) }}
          />
        )}

        {!isError &&
          Array.from(groupedByZone.entries()).map(([zoneName, zoneSlots]) => (
            <div key={zoneName} className="space-y-2">
              <h2 className="text-sm font-semibold text-stone-700">{zoneName}</h2>
              <AdminDataTable
                data={zoneSlots}
                columns={columns}
                isLoading={isLoading}
              />
            </div>
          ))}

        {!isError && isLoading && (
          <AdminDataTable
            data={[]}
            columns={columns}
            isLoading={true}
          />
        )}
      </div>

      {/* Create modal */}
      <SlotFormModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        slot={null}
        zones={zones}
        onSave={handleCreateSave}
        isSaving={createSlot.isPending}
      />

      {/* Edit modal */}
      <SlotFormModal
        open={editingSlot !== null}
        onClose={() => setEditingSlot(null)}
        slot={editingSlot}
        zones={zones}
        onSave={handleEditSave}
        isSaving={updateSlot.isPending}
      />

      {/* Delete confirmation */}
      <AdminModal
        open={deletingSlotId !== null}
        onClose={() => setDeletingSlotId(null)}
        title="Supprimer le créneau"
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setDeletingSlotId(null)}
              disabled={deleteSlot.isPending}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleteSlot.isPending}
              className="gap-1.5 bg-red-600 hover:bg-red-700"
            >
              {deleteSlot.isPending ? (
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : null}
              Supprimer
            </Button>
          </>
        }
      >
        <p className="text-sm text-stone-700">
          Êtes-vous sûr de vouloir supprimer ce créneau ? Cette action est
          irréversible.
        </p>
      </AdminModal>
    </AdminLayout>
  );
}
